'use client';

import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import useFetchComponentPricing from '../../../hooks/useFetchComponentPricing';
import axios from 'axios';
import { toast } from 'react-toastify';

// Field mapping from row label to backend property
const fieldMapping = {
  'Hot Bends': 'HotBends',
  'L/R Set (w/ barrel)': 'LrSetWithBarrel',
  'L/R Set': 'LrSet',
  'Mainline Valve Assembly': 'MainlineValveAssembly',
  Futures: 'Futures',
};

// PATCH request for a single row
const useUpdateComponentPricing = () => {
  const updatePrice = async (id, patchDoc) => {
    try {
      const response = await axios.patch(`/api/PipelinePricing/component/update-pricing/${id}`, patchDoc, {
        headers: {
          'Content-Type': 'application/json-patch+json',
        },
      });
      return { success: true, message: response.data };
    } catch (error) {
      console.error('Update failed:', error);
      return { success: false, message: error?.response?.data || error.message };
    }
  };

  return { updatePrice };
};

const ComponentPricingGrid = () => {
  const { transposedData, columnDefs, loading, error } = useFetchComponentPricing();
  const { updatePrice } = useUpdateComponentPricing();
  const [editedRows, setEditedRows] = useState(new Map());
  const [updating, setUpdating] = useState(false);

  const handleGridEdit = (event) => {
    const { data, colDef, newValue } = event;
    if (colDef.field === 'category') return;

    const cell = data[colDef.field];
    if (!cell || !cell.id) {
      console.warn('No ID found for cell:', data.category, colDef.field);
      return;
    }

    const fieldName = fieldMapping[data.category.trim()];
    if (!fieldName) {
      console.warn(`No field mapping found for category: "${data.category.trim()}"`);
      return;
    }

    const updated = new Map(editedRows);
    if (!updated.has(cell.id)) {
      updated.set(cell.id, []);
    }

    updated.get(cell.id).push({
      pipeSize: parseFloat(colDef.field),
      fieldName,
      price: parseFloat(newValue),
    });

    setEditedRows(updated);
  };

  const handleUpdatePricing = async () => {
    if (editedRows.size === 0) {
      toast.info('No changes to update.');
      return;
    }

    setUpdating(true);

    const patchPromises = Array.from(editedRows.entries()).map(([id, changes]) => {
      const patchDoc = [{ op: 'replace', path: '/PipeSize', value: changes[0].pipeSize }];

      changes.forEach((change) => {
        patchDoc.push({
          op: 'replace',
          path: `/${change.fieldName}`,
          value: change.price,
        });
      });

      return updatePrice(id, patchDoc);
    });

    const results = await Promise.allSettled(patchPromises);

    let allSucceeded = true;
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        if (!result.value.success) {
          allSucceeded = false;
          const row = Array.from(editedRows.values())[index][0];
          toast.error(`Failed to update row ID ${row.id}: ${result.value.message}`);
        }
      } else {
        allSucceeded = false;
        const row = Array.from(editedRows.values())[index][0];
        toast.error(`Failed to update row ID ${row.id}: ${result.reason}`);
      }
    });

    if (allSucceeded) {
      toast.success('Component prices updated!');
      setEditedRows(new Map());
    }

    setUpdating(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        mb={2}
        sx={{
          backgroundColor: '#0C1D3C',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 1,
        }}
      >
        Component Pricing Table
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error.message || error.toString()}</Alert>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Box className="ag-theme-alpine" style={{ width: '100%', overflowX: 'auto' }}>
              <AgGridReact
                rowData={transposedData}
                columnDefs={columnDefs}
                suppressFieldDotNotation={true}
                onCellValueChanged={handleGridEdit}
                defaultColDef={{ resizable: true, minWidth: 100 }}
                domLayout="autoHeight"
                getRowNodeId={(data) => data.category}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleUpdatePricing} disabled={updating}>
              {updating ? 'Updating...' : 'Update Pricing'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ComponentPricingGrid;
